import { useState, useRef, useCallback } from 'react';
import { Editor, Path } from 'slate';
import { ShortLinkElement, insertShortLink, updateShortLink } from '../plugins/with-shortlinks';
import { Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

interface Parameter {
  name: string;
  value: string;
}

export function useShortLink(editor: Editor) {
  const [baseUrl, setBaseUrl] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [parameters, setParameters] = useState<Parameter[]>([
    { name: 'utm_source', value: 'contextual' },
    { name: 'utm_medium', value: 'sms' }
  ]);
  const [editingParameterIndex, setEditingParameterIndex] = useState<number | null>(null);
  const [hasCopied, setHasCopied] = useState(false);

  // Save the current editor selection before the dialog opens
  const savedSelectionRef = useRef(editor.selection);
  // Track the editing path when editing an existing shortlink
  const editingPathRef = useRef<Path | null>(null);

  const captureSelection = useCallback(() => {
    savedSelectionRef.current = editor.selection;
  }, [editor]);

  // Load an existing shortlink's data into the form
  const loadShortLink = useCallback((element: ShortLinkElement, path: Path) => {
    editingPathRef.current = path;
    setBaseUrl(element.originalData.baseUrl);
    setParameters(element.originalData.parameters);
    setErrors({});
    setEditingParameterIndex(null);
    setHasCopied(false);
  }, []);

  // Consolidated validation function
  const getValidationErrors = useCallback(
    (baseUrl: string, parameters: Parameter[]): Record<string, string> => {
      const errs: Record<string, string> = {};
      // Validate baseUrl
      if (!baseUrl) {
        errs.url = 'Please enter a URL';
      } else if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
        errs.url = 'URL must start with http:// or https://';
      }

      // Validate parameters
      const seenParams = new Set<string>();
      parameters.forEach((param, index) => {
        if (!param.name && !param.value) {
          errs[`param${index}`] = 'Parameter name and value are required';
        } else if (!param.name && param.value) {
          errs[`param${index}`] = 'Parameter name is required';
        } else if (!param.value && param.name) {
          errs[`param${index}`] = 'Parameter value is required';
        } else if (seenParams.has(param.name)) {
          errs[`param${index}`] = 'Duplicate parameter name';
        }
        seenParams.add(param.name);
      });

      return errs;
    },
    []
  );

  const addParameter = useCallback(() => {
    setParameters((prev) => [...prev, { name: '', value: '' }]);
  }, []);

  const removeParameter = useCallback((index: number) => {
    setParameters((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateParameter = useCallback((index: number, field: 'name' | 'value', value: string) => {
    setParameters((prev) => {
      const newParameters = [...prev];
      newParameters[index][field] = value;
      return newParameters;
    });
  }, []);

  const getPreviewUrl = useCallback(() => {
    if (!baseUrl) return '';
    const validParams = parameters.filter((param) => param.name && param.value);
    if (validParams.length === 0) return baseUrl;
    const queryString = validParams
      .map((param) => `${encodeURIComponent(param.name)}=${encodeURIComponent(param.value)}`)
      .join('&');
    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${queryString}`;
  }, [baseUrl, parameters]);

  const getHumanReadablePreview = useCallback(() => {
    if (!baseUrl) return '';
    const validParams = parameters.filter((param) => param.name && param.value);
    if (validParams.length === 0) return baseUrl;
    const queryString = validParams.map((param) => `${param.name}=${param.value}`).join('\n  & ');
    return `${baseUrl}${baseUrl.includes('?') ? '\n  & ' : '\n  ? '}${queryString}`;
  }, [baseUrl, parameters]);

  const handleSubmit = useCallback(() => {
    const validationErrors = getValidationErrors(baseUrl, parameters);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return false;
    }
    setErrors({});

    // Mock shortener service for now
    const mockShorten = (_url: string) => {
      return `short.link/${Math.random().toString(36).substring(7)}`;
    };

    try {
      const shortUrl = mockShorten(getPreviewUrl());

      // Restore the selection if it's lost
      if (!editor.selection && savedSelectionRef.current) {
        Transforms.select(editor, savedSelectionRef.current);
      }
      // Focus the editor before modifying
      ReactEditor.focus(editor);

      const originalData = {
        baseUrl,
        parameters
      };

      if (editingPathRef.current) {
        // Update existing shortlink
        updateShortLink(editor, editingPathRef.current, shortUrl, originalData);
      } else {
        // Insert new shortlink
        insertShortLink(editor, shortUrl, originalData);
      }

      return true;
    } catch (_error) {
      setErrors({ general: 'Failed to create short link' });
      return false;
    }
  }, [baseUrl, parameters, editor, getPreviewUrl, getValidationErrors]);

  const handleCopyUrl = useCallback(async () => {
    const url = getPreviewUrl();
    await navigator.clipboard.writeText(url);
    setHasCopied(true);
    setTimeout(() => {
      setHasCopied(false);
    }, 2000);
  }, [getPreviewUrl]);

  const resetState = useCallback(() => {
    setBaseUrl('');
    setErrors({});
    setParameters([
      { name: 'utm_source', value: 'contextual' },
      { name: 'utm_medium', value: 'sms' }
    ]);
    setEditingParameterIndex(null);
    setHasCopied(false);
    editingPathRef.current = null;
  }, []);

  return {
    baseUrl,
    setBaseUrl,
    errors,
    parameters,
    editingParameterIndex,
    setEditingParameterIndex,
    hasCopied,
    addParameter,
    removeParameter,
    updateParameter,
    getPreviewUrl,
    getHumanReadablePreview,
    handleSubmit,
    handleCopyUrl,
    resetState,
    captureSelection,
    loadShortLink,
    isEditing: editingPathRef.current !== null
  };
}
